import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  computeDistanceBetween, computeHeading, computeOffset,
} from 'spherical-geometry-js';
import { type LatLng } from '~/_shared/types/latLng';
import { type IndividualRadiusProximity } from '~/_shared/types/proximity/proximity.types';
import { createUuid } from '~/_shared/utils/createUuid';
import { KeyboardKeys } from '~/_shared/utils/hooks/useKeyPress';
import {
  type MapKeyPressCallback, MapKeyPressPriority, useMapKeyPressContext,
} from '~/_shared/utils/hooks/useMapKeyPressContext';
import { notNullsy } from '~/_shared/utils/typeGuards';
import {
  convertMetersToUnit, convertUnitToMeters,
} from '~/_shared/utils/unitSystem/unitSystem.helpers';
import { type MapOutlineInstance } from '~/map/map/mapObjects/mapOutline/mapOutline.component';
import { useProjectionOverlay } from '~/map/map/useProjectionOverlay';
import {
  endIndividualRadiusResizeHover, endIndividualRadiusResizingDnDMode, startIndividualRadiusResizeHover,
  startIndividualRadiusResizingDnDMode,
} from '~/store/frontendState/proximityTool/proximityTool.actionCreators';
import { modifyProximity } from '~/store/mapSettings/proximity/mapSettingsProximity.actionCreators';

export const RESIZE_DOT_APPEAR_DISTANCE = 10;

const roundRadius = (radius: number): number => {
  const decimalFactor = radius < 1 ? 1000 : radius < 10 ? 100 : 10;
  return Math.round(radius * decimalFactor) / decimalFactor;
};

export const useProximityResize = ({
  appearDistanceInPixels = RESIZE_DOT_APPEAR_DISTANCE,
  center,
  isEnabled,
  map,
  proximity,
}: {
  appearDistanceInPixels?: number;
  center: LatLng;
  isEnabled: boolean;
  map: google.maps.Map | undefined;
  proximity: IndividualRadiusProximity;
}) => {
  const dispatch = useDispatch();
  const { getDistanceBetweenPoints } = useProjectionOverlay(map);

  const [outlineId] = useState(() => createUuid());
  const [markerLatLng, setMarkerLatLng] = useState<LatLng | null>(null);
  const [isResizeMarkerHovered, setIsResizeMarkerHovered] = useState(false);
  const [draggedRadius, setDraggedRadius] = useState<number | null>(null);

  const isDraggingRef = useRef(false);
  const draggedLatLngRef = useRef<LatLng | null>(null);
  const centerRef = useRef(center);
  centerRef.current = center;
  const proximityRef = useRef(proximity);
  proximityRef.current = proximity;

  // Show the resize marker on the closest point of the circle's border once the cursor gets near it.
  useEffect(() => {
    if (!map || !isEnabled) {
      setMarkerLatLng(null);
      return undefined;
    }

    const mouseMoveListener = map.addListener('mousemove', (e: google.maps.MapMouseEvent) => {
      if (isDraggingRef.current || !e.latLng) {
        return;
      }

      const cursorLatLng: LatLng = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      const radiusInMeters = convertUnitToMeters(proximityRef.current.data.radius, proximityRef.current.data.unit);
      const heading = computeHeading(centerRef.current, cursorLatLng);
      const borderLatLng = computeOffset(centerRef.current, radiusInMeters, heading);
      const borderPoint: LatLng = { lat: borderLatLng.lat(), lng: borderLatLng.lng() };

      const distanceToBorder = getDistanceBetweenPoints(cursorLatLng, borderPoint);

      setMarkerLatLng(notNullsy(distanceToBorder) && distanceToBorder <= appearDistanceInPixels ? borderPoint : null);
    });

    return () => mouseMoveListener.remove();
  }, [map, isEnabled, getDistanceBetweenPoints, appearDistanceInPixels]);

  const getRadiusForLatLng = useCallback((latLng: LatLng) => {
    const radiusInMeters = computeDistanceBetween(centerRef.current, latLng);
    return roundRadius(convertMetersToUnit(radiusInMeters, proximityRef.current.data.unit));
  }, []);

  const onResizeDragStart = useCallback((_outlineId: Uuid, latLng: LatLng) => {
    isDraggingRef.current = true;
    draggedLatLngRef.current = latLng;
    setMarkerLatLng(latLng);
    setDraggedRadius(getRadiusForLatLng(latLng));
    dispatch(startIndividualRadiusResizingDnDMode());
  }, [dispatch, getRadiusForLatLng]);

  const onDragCleanup = useCallback(() => {
    isDraggingRef.current = false;
    draggedLatLngRef.current = null;
    setMarkerLatLng(null);
    setDraggedRadius(null);
    dispatch(endIndividualRadiusResizingDnDMode());
  }, [dispatch]);

  const onResizeDragMove = useCallback((_outlineId: Uuid, latLng: LatLng) => {
    if (!isDraggingRef.current) {
      return;
    }

    draggedLatLngRef.current = latLng;
    setMarkerLatLng(latLng);
    setDraggedRadius(getRadiusForLatLng(latLng));
  }, [getRadiusForLatLng]);

  const endDrag = useCallback(() => {
    isDraggingRef.current = false;

    const draggedLatLng = draggedLatLngRef.current;
    if (draggedLatLng) {
      dispatch(modifyProximity({
        ...proximityRef.current,
        data: {
          ...proximityRef.current.data,
          radius: getRadiusForLatLng(draggedLatLng),
        },
      }));
    }

    onDragCleanup();
  }, [dispatch, getRadiusForLatLng, onDragCleanup]);

  const onResizeDragEnd = useCallback((_outlineId: Uuid) => endDrag(), [endDrag]);

  const cancelDrag = useCallback(() => {
    onDragCleanup();
  }, [onDragCleanup]);

  const { addMapKeyHandler } = useMapKeyPressContext();
  const isResizeDragActive = notNullsy(draggedRadius);

  useEffect(() => { // Cancel the resize on ESC
    if (!isResizeDragActive) {
      return undefined;
    }

    const onEsc: MapKeyPressCallback = (e) => {
      cancelDrag();
      e.stopPropagation();
    };

    const removeKeyHandler = addMapKeyHandler(KeyboardKeys.Escape, MapKeyPressPriority.ResetDrawing, onEsc);
    return removeKeyHandler;
  }, [addMapKeyHandler, cancelDrag, isResizeDragActive]);

  // Nothing else releases the map options pushed in onResizeDragStart if this unmounts mid-drag
  useEffect(() => {
    return () => {
      if (isDraggingRef.current) {
        endDrag();
      }
    };
  }, [endDrag]);

  const onResizeMouseOver = useCallback((_outlineId: Uuid) => {
    setIsResizeMarkerHovered(true);
    dispatch(startIndividualRadiusResizeHover());
  }, [dispatch]);

  const onResizeMouseOut = useCallback((_outlineId: Uuid) => {
    setIsResizeMarkerHovered(false);
    dispatch(endIndividualRadiusResizeHover());
  }, [dispatch]);

  const resizeMarkerOutline: MapOutlineInstance | null = useMemo(() => (
    markerLatLng && {
      id: outlineId,
      lat: markerLatLng.lat,
      lng: markerLatLng.lng,
    }), [markerLatLng, outlineId]);

  return {
    resizeMarkerOutline,
    isResizeMarkerHovered,
    draggedRadius,
    onResizeDragStart,
    onResizeDragMove,
    onResizeDragEnd,
    onResizeMouseOver,
    onResizeMouseOut,
  };
};
